import { Crown } from 'lucide-react-native';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Platform,
  Pressable,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
  type ViewStyle,
} from 'react-native';

import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';

import {
  founderBadgeAccessibilityLabel,
  founderBadgeTooltipText,
} from './founder-badge-copy';
import {
  verifiedBadgeTooltipLeftOffset,
  verifiedBadgeTooltipWidth,
} from './verified-badge-layout';

const TOOLTIP_ESTIMATED_HEIGHT = 72;
const TOOLTIP_GAP = 8;
const FOUNDER_BADGE_COLOR = '#f6c343';
const FOUNDER_TOOLTIP_BACKGROUND = '#f6c343';
const WEB_TOOLTIP_LAYER_STYLE: ViewStyle = {
  bottom: 0,
  left: 0,
  pointerEvents: 'none',
  position: 'fixed' as ViewStyle['position'],
  right: 0,
  top: 0,
  zIndex: 2147483647,
};

export function FounderBadge({
  founder,
  size = 16,
  tooltipPlacement = 'above',
}: {
  founder?: boolean | null;
  size?: number;
  tooltipPlacement?: 'above' | 'below';
}) {
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<{
    left: number;
    top: number;
  }>({
    left: spacing.sm,
    top: spacing.sm,
  });
  const containerRef = useRef<View>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { height: viewportHeight, width: viewportWidth } =
    useWindowDimensions();
  const viewportMargin = spacing.sm;
  const tooltipWidth = verifiedBadgeTooltipWidth({
    viewportMargin,
    viewportWidth,
  });

  const clearTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const hideTooltip = useCallback(() => {
    clearTimer();
    setTooltipVisible(false);
  }, [clearTimer]);

  const updateTooltipPosition = useCallback(() => {
    containerRef.current?.measureInWindow((x, y, width, height) => {
      const leftOffset = verifiedBadgeTooltipLeftOffset({
        badgeWidth: width,
        badgeX: x,
        tooltipWidth,
        viewportMargin,
        viewportWidth,
      });
      const aboveTop = y - TOOLTIP_ESTIMATED_HEIGHT - TOOLTIP_GAP;
      const belowTop = y + height + TOOLTIP_GAP;
      const preferredTop = tooltipPlacement === 'below' ? belowTop : aboveTop;
      const fallbackTop = tooltipPlacement === 'below' ? aboveTop : belowTop;
      const preferredFits =
        preferredTop >= viewportMargin &&
        preferredTop + TOOLTIP_ESTIMATED_HEIGHT <=
          viewportHeight - viewportMargin;
      const top = Math.min(
        Math.max(preferredFits ? preferredTop : fallbackTop, viewportMargin),
        Math.max(
          viewportMargin,
          viewportHeight - viewportMargin - TOOLTIP_ESTIMATED_HEIGHT,
        ),
      );

      setTooltipPosition({
        left: x + leftOffset,
        top,
      });
    });
  }, [
    tooltipPlacement,
    tooltipWidth,
    viewportHeight,
    viewportMargin,
    viewportWidth,
  ]);

  const showTooltip = useCallback(() => {
    clearTimer();
    updateTooltipPosition();
    setTooltipVisible(true);
    timeoutRef.current = setTimeout(() => {
      setTooltipVisible(false);
      timeoutRef.current = null;
    }, 3200);
  }, [clearTimer, updateTooltipPosition]);

  useEffect(() => {
    return () => {
      clearTimer();
    };
  }, [clearTimer]);

  if (!founder) {
    return null;
  }

  return (
    <View
      ref={containerRef}
      style={[
        styles.container,
        { height: size, width: size },
        tooltipVisible ? styles.containerActive : null,
      ]}
    >
      <Pressable
        accessibilityHint="Shows what this founder icon means."
        accessibilityLabel={founderBadgeAccessibilityLabel()}
        accessibilityRole="button"
        hitSlop={6}
        onHoverIn={Platform.OS === 'web' ? showTooltip : undefined}
        onHoverOut={Platform.OS === 'web' ? hideTooltip : undefined}
        onPress={(event) => {
          event.stopPropagation();
          showTooltip();
        }}
        style={({ pressed }) => [
          styles.badge,
          { height: size, width: size },
          pressed ? styles.badgePressed : null,
        ]}
      >
        <Crown
          color={FOUNDER_BADGE_COLOR}
          fill={FOUNDER_BADGE_COLOR}
          size={size}
          strokeWidth={2.4}
        />
      </Pressable>
      {tooltipVisible ? (
        <FounderBadgeTooltipPortal>
          <View pointerEvents="none" style={styles.tooltipLayer}>
            <View
              style={[
                styles.tooltip,
                {
                  left: tooltipPosition.left,
                  top: tooltipPosition.top,
                  width: tooltipWidth,
                },
              ]}
            >
              <Text style={styles.tooltipText}>
                {founderBadgeTooltipText()}
              </Text>
            </View>
          </View>
        </FounderBadgeTooltipPortal>
      ) : null}
    </View>
  );
}

function FounderBadgeTooltipPortal({
  children,
}: {
  children: React.ReactNode;
}) {
  if (Platform.OS !== 'web' || typeof document === 'undefined') {
    return <>{children}</>;
  }

  return createPortal(
    <View pointerEvents="none" style={WEB_TOOLTIP_LAYER_STYLE}>
      {children}
    </View>,
    document.body,
  );
}

const styles = StyleSheet.create({
  badge: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  badgePressed: {
    opacity: 0.72,
  },
  container: {
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'visible',
    position: 'relative',
    zIndex: 45,
  },
  containerActive: {
    elevation: 1000,
    zIndex: 100000,
  },
  tooltip: {
    backgroundColor: FOUNDER_TOOLTIP_BACKGROUND,
    borderColor: colors.surface,
    borderRadius: 8,
    borderWidth: 1,
    elevation: 1000,
    paddingHorizontal: spacing.sm,
    paddingVertical: 7,
    position: 'absolute',
    shadowOpacity: 0,
    zIndex: 100001,
  },
  tooltipLayer: {
    ...StyleSheet.absoluteFillObject,
    elevation: 1000,
    position: 'absolute',
    zIndex: 100000,
  },
  tooltipText: {
    color: colors.text,
    fontSize: 12,
    fontWeight: '900',
    lineHeight: 16,
    textAlign: 'center',
  },
});
